/**
 * 연관 계약 섹션
 * 같은 계약관리(1:N)에 묶인 원계약 + 재계약을 계약시작일 순으로 보여주고,
 * 마지막 계약에서만 재계약을 등록할 수 있게 한다.
 */

import moment from "moment";
import { useMemo } from "react";
import { Cell, useBlockLayout, useTable } from "react-table";
import { DetailTotalData, SiblingContract } from "src/api/contract/contract-types";
import { BaseButton, BaseTooltip } from "src/components";
import { BaseContractStatus } from "src/components/BaseContractStatus";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import BaseSection from "src/components/layout/BaseSection";
import PagePath from "src/pagePath.json";
import { ViewYmdFormat } from "src/utils";
import useContractRenewal from "../../hooks/useContractRenewal";

type Props = {
  detailData: DetailTotalData; // 계약 상세 조회 정보
  onClickRenewal: () => void; // 재계약 생성 모달 열기
};

const RENEWAL_TOOLTIP = "재계약은 마지막 계약에서 등록할 수 있습니다.";

const RelatedContractsSection = ({ detailData, onClickRenewal }: Props) => {
  const { siblingContracts, isRenewalVisible, isRenewable } = useContractRenewal(detailData);

  // 현재 열려 있는 계약 ID. 형제 계약과 타입(문자열/숫자)이 달라도 매칭되도록 숫자로 비교한다
  const currentContractId = Number(detailData?.contract?.contractId);

  // 테이블은 계약 ID 오름차순으로 표시한다 (판정에 쓰이는 siblingContracts는 계약시작일 오름차순 유지)
  const tableData = useMemo(
    () => [...siblingContracts].sort((a, b) => Number(a.contractId) - Number(b.contractId)),
    [siblingContracts],
  );

  const columns: Array<any> = useMemo(
    () => [
      {
        Header: "계약 ID",
        accessor: "contractId",
        Cell: ({ value }: Cell<SiblingContract>) => {
          const path = PagePath.contract.detail.replace(":id", String(value));
          return <BaseNewTabLink path={path} value={String(value)} />;
        },
        width: 100,
      },
      {
        Header: "계약상태",
        accessor: "contractStep",
        Cell: ({ value }: Cell<SiblingContract>) => (
          <div className="w-100 flex-center-center">
            <BaseContractStatus status={value} />
          </div>
        ),
        width: 100,
      },
      {
        Header: "계약명",
        accessor: "contractName",
        Cell: ({ value }: Cell<SiblingContract>) => <BaseTooltip contents={value || "-"} isSingleLine />,
        width: 275,
      },
      {
        Header: "계약시작일",
        accessor: "contractStartTime",
        Cell: ({ value }: Cell<SiblingContract>) => <p>{value ? moment(value).format(ViewYmdFormat.YYYY_MM_DD_HH_MM) : "-"}</p>,
        width: 160,
      },
      {
        Header: "계약종료일",
        accessor: "contractEndTime",
        Cell: ({ value }: Cell<SiblingContract>) => <p>{value ? moment(value).format(ViewYmdFormat.YYYY_MM_DD_HH_MM) : "-"}</p>,
        width: 160,
      },
    ],
    [],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: tableData,
    },
    useBlockLayout,
  );

  // 공간예약 / 관리비 상품은 재계약 대상이 아니므로 섹션 자체를 노출하지 않는다
  if (!isRenewalVisible) return null;

  return (
    <div className="flex-start">
      <BaseSection
        titleChildren={
          <div className="flex-center">
            <p>연관 계약</p>
            <BaseTooltip touchIcon={"QUESTION"} className="ml4 mt1" size={16} tooltip={RENEWAL_TOOLTIP} />
          </div>
        }
        children={
          <div className="w-100">
            <div className="flex-center mb20">
              <BaseButton title="재계약" className="color-white size-medium" disabled={!isRenewable} onClick={onClickRenewal} />
            </div>
            <div {...getTableProps()} className="base-table sticky">
              <div className="header">
                {headerGroups.map((headerGroup) => (
                  <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                    {headerGroup.headers.map((header) => (
                      <div {...header.getHeaderProps()} className="base-table__th">
                        {header.render("Header")}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div {...getTableBodyProps()} className="body">
                {rows.map((row: any) => {
                  prepareRow(row);
                  // 현재 보고 있는 계약을 하이라이트
                  const isCurrent =
                    !Number.isNaN(currentContractId) && Number(row.original?.contractId) === currentContractId;
                  return (
                    <div {...row.getRowProps()} className={`base-table__tr ${isCurrent ? "base-table__tr--current" : ""}`}>
                      {row.cells.map((cell: any) => (
                        <div {...cell.getCellProps()} className="base-table__td">
                          {cell.render("Cell")}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default RelatedContractsSection;
